/**
 * Transfer code: what the QR contains and what the user copies/pastes.
 *
 * Format: `CSYP1-<CODIGO_SALA>-<CLAVE_base64url>`
 *  - `CSYP1`: prefix + version (allows the format to evolve).
 *  - `CODIGO_SALA`: short signaling-room code (public ID only).
 *  - `CLAVE`: one-time AES-256 key in base64url — it NEVER travels to the server;
 *    it exists only in the QR/clipboard between the two devices.
 */

import { base64UrlToBytes, bytesToBase64Url } from "./encoding";
import { TRANSFER_KEY_BYTES } from "./crypto";

export const TRANSFER_CODE_PREFIX = "CSYP1";

export interface ParsedTransferCode {
  roomCode: string;
  keyBytes: Uint8Array;
}

export function buildTransferCode(roomCode: string, keyBytes: Uint8Array): string {
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    throw new Error("Invalid transfer key length");
  }
  if (!/^[A-Z2-9]{4,16}$/.test(roomCode)) {
    throw new Error("Invalid room code");
  }
  return `${TRANSFER_CODE_PREFIX}-${roomCode}-${bytesToBase64Url(keyBytes)}`;
}

/** Returns `null` if the text is not a valid transfer code. */
export function parseTransferCode(raw: string): ParsedTransferCode | null {
  const trimmed = raw.trim();
  const match = /^CSYP1-([A-Za-z2-9]{4,16})-([A-Za-z0-9_-]{43})$/.exec(trimmed);
  if (!match) {
    return null;
  }
  let keyBytes: Uint8Array;
  try {
    keyBytes = base64UrlToBytes(match[2]!);
  } catch {
    return null;
  }
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    return null;
  }
  return { roomCode: match[1]!.toUpperCase(), keyBytes };
}
